import { notificationService } from '@/services/notificationService'
import { validatePassword } from '@/utils/validation'
import { type ChangeEvent, type FormEvent, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useAccessStore from '../../store/accessStore'

import CreateNewPasswordForm from './CreateNewPasswordForm'
import styles from './forgotPassword.module.css'

interface CreateNewPasswordProps {
  onLinkClick: (page: string) => void
  onSuccess: () => void
}

interface CreateNewPasswordValues {
  newPassword: string
  confirmNewPassword: string
  [key: string]: string
}

interface CreateNewPasswordErrors {
  newPassword?: string
  confirmNewPassword?: string
  general?: string
  [key: string]: string | undefined
}

export const CreateNewPassword = ({ onLinkClick, onSuccess }: CreateNewPasswordProps) => {
  const location = useLocation()
  const email = location.state?.email

  const [values, setValues] = useState<CreateNewPasswordValues>({
    newPassword: '',
    confirmNewPassword: '',
  })
  const [errors, setErrors] = useState<CreateNewPasswordErrors>({})
  const [generalError, setGeneralError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { createNewPassword, login } = useAccessStore()

  const validateForm = (): boolean => {
    const newErrors: CreateNewPasswordErrors = {}

    const passwordError = validatePassword(values.newPassword)
    if (passwordError) {
      newErrors.newPassword = passwordError
      notificationService.error(passwordError)
    }

    const confirmPasswordError = validatePassword(values.confirmNewPassword)
    if (confirmPasswordError && !passwordError) {
      newErrors.confirmNewPassword = confirmPasswordError
      notificationService.error(confirmPasswordError)
    }

    if (values.newPassword && values.confirmNewPassword && values.newPassword !== values.confirmNewPassword) {
      const errorMsg = 'Passwords do not match'
      newErrors.confirmNewPassword = errorMsg
      if (!passwordError && !confirmPasswordError) {
        notificationService.error(errorMsg)
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setValues((prev) => ({
      ...prev,
      [id]: value,
    }))

    if (errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: '',
      }))
    }

    if (generalError) {
      setGeneralError('')
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsLoading(true)
      const createNewPasswordResponse = await createNewPassword(values.newPassword, values.confirmNewPassword)

      if (createNewPasswordResponse === 200) {
        notificationService.success('Successfully created new password!')
        const loginResponse = await login(email, values.newPassword)
        if (loginResponse && 'ok' in loginResponse && loginResponse.ok && onSuccess) onSuccess()
      }
    } catch (error: unknown) {
      console.error('Login error:', error)
      const responseData = (error as { response?: { data?: Record<string, unknown> } }).response?.data

      if (responseData && typeof responseData.data === 'object' && responseData.data) {
        const backendErrors = responseData.data as Record<string, unknown>
        const newErrors: CreateNewPasswordErrors = {}

        for (const [key, value] of Object.entries(backendErrors)) {
          if (typeof value === 'string') {
            newErrors[key] = value
          }
        }

        setErrors(newErrors)
        notificationService.error('Please check your credentials')
      } else {
        if (String(error) === 'Error: New password creation failed: Request failed with status code 422') {
          setErrors({
            confirmNewPassword: 'New password cannot be the same as old password',
          })
        }
        notificationService.error('Faild to create new password')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.mainContentWrapper}>
      <h1 className={styles.title}>Create new password</h1>
      <p className={styles.textGrey}>Enter your new password</p>

      <CreateNewPasswordForm
        values={values}
        // errors={errors}
        // generalError={generalError}
        isLoading={isLoading}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />

      <div className={styles.additional}>
        <p className={styles.text}>Don't have an account?</p>
        <button
          type="button"
          className={styles.link}
          onClick={() => onLinkClick('registration')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onLinkClick('registration')
            }
          }}
        >
          Sign up
        </button>
      </div>
    </div>
  )
}
